import React, { createContext, useContext, useEffect } from "react";
import Cookies from "universal-cookie";
import {
  changePassword,
  deactivateAccount,
  deactivationFeedback,
  forgetPassword,
  resentPassword,
  login,
  verifyCode,
  resendEmail,
  resendCode,
  resetPassword,
  loginSSO,
  verifyToken,
  getUserAccess,
} from "../services/auth.services";
import { useState } from "react";
import {
  ERROR,
  LOADING,
  SUCCESS,
  apiUrl,
  domain,
  treatment_program,
} from "../constants/common";
import { useAlert } from "./Alert";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import paths from "../constants/paths";
import { identifyUser, resetMixpanel } from "../mixpanel";

const AuthContext = createContext({});
const cookies = new Cookies();
let intervalId;

export const AuthContextProvider = ({ children }) => {
  const { sendAlert } = useAlert();
  const navigate = useNavigate();
  const initialUserData = {
    first_name: "",
    last_name: "",
  };

  const [authState, setAuthState] = useState({
    loginStatus: "",
    userData: initialUserData,
    forgetPasswordStatus: "",
    email: "",
    resetPasswordStatus: "",
    isLoggedIn: JSON.parse(localStorage.getItem("isLoggedIn") || "false"),
    account_type: localStorage.getItem("account_type") || "",
    post_account_type: localStorage.getItem("post_account_type") || "",
    resendEmailStatus: "",
    isLogginBlocked: true,
    loginBlockDate: localStorage.getItem("loginBlockDate") || "",
    countDown: "30:00",
    updatePasswordStatus: "",
    deactivateAccountStatus: "",
    isCodeSent: false,
    tempUserData: {},
    isVerificationStep: false,
    permissions: null,
    permissionsStatus: "",
  });
  const [step, setStep] = useState(parseInt(localStorage.getItem("resetStep") || 1));

  const next = () => {
    setStep((prevState) => prevState + 1);
  };

  useEffect(() => {
    if (!authState?.email) setStep(1);
    else localStorage.setItem("resetStep", step);
  }, [step]);

  const prev = () => {
    setStep((prevState) => {
      const newStep = prevState > 1 ? prevState - 1 : prevState;
      localStorage.removeItem("resetStep");
      return newStep;
    });
  };

  const handleState = (obj) => {
    setAuthState((prevState) => ({
      ...prevState,
      ...obj,
    }));
  };

  const isMentalHealthProfessional = () => {
    return JSON.parse(
      localStorage.getItem("isMentalHealthProfessional") || false
    );
  };

  const signIn = async (data) => {
    handleState({ loginStatus: LOADING });

    try {
      const response = await login(data);
      if (response?.data?.message === "success") {
        handleState({
          loginStatus: SUCCESS,
          isVerificationStep: true,
          email: data.email,
        });
        sendAlert(`${response.data?.data?.message}`, "success");
        setStep(2);
      } else if (response.data?.data?.error) {
        sendAlert(response.data.data.error, "error");
        handleBlockLogin(response.data.data.error);
      } else {
        identifyUser(response.data.data?.user);
        handleSuccessfulLogin(response);
      }
    } catch (error) {
      console.log('error', error);
      if (error.response?.status === 403 && error.response?.data?.deactivated === 1) {
        handleState({
          loginStatus: 'DEACTIVATED',
          deactivatedMessage: error.response.data.message || "Your account has been deactivated"
        });
        return;
      }
      handleLoginError(error);
    }
  };

  const signInSSO = async () => {
    handleState({ loginStatus: LOADING });

    try {
      const response = await loginSSO();
      if (response?.data?.message === "SUCCESS") {
        return response?.data;
      } else if (response.data?.data?.error) {
        sendAlert(response.data.data.error, "error");
      }
    } catch (error) {
      console.log('error', error);
      if (error.response?.status === 403 && error.response?.data?.deactivated === 1) {
        handleState({
          loginStatus: 'DEACTIVATED',
          deactivatedMessage: error.response.data.message || "Your account has been deactivated"
        });
        return;
      }
      handleLoginError(error);
    }
  };

  const verifyUserToken = async () => {
    handleState({ loginStatus: LOADING });
    try {
      const response = await verifyToken();
      if (response.data?.message === "success") {
        await handleSuccessfulLogin(response, localStorage?.getItem('accessToken'))
        navigate(paths.home);
      } else {
        sendAlert("Invalid verification code. Please try again.", "error");
        handleState({ loginStatus: ERROR });
        console.log('error', ERROR);
        navigate(paths.login);
      }
    } catch (error) {
      console.log('error', ERROR);
      handleLoginError(error);
      navigate(paths.home);
    }
  }
  // const signInSSO = () => {
  //   // Redirect directly to backend SAML login
  //   window.location.href = `${apiUrl}saml/login`;
  // };

  const verifyLoginCode = async (code) => {
    handleState({ loginStatus: LOADING });
    try {
      const response = await verifyCode({ code, email: authState?.email });

      if (response.data?.message === "success") {
        identifyUser(response.data.data?.user);
        handleSuccessfulLogin(response);
      } else {
        sendAlert("Invalid verification code. Please try again.", "error");
        handleState({ loginStatus: ERROR });
      }
    } catch (error) {
      handleLoginError(error);
    }
  };

  const resendCodeVerification = async () => {
    handleState({ resendEmailStatus: LOADING });
    try {
      const response = await resendCode({ email: authState?.email });
      if (response.status === 200) {
        sendAlert(
          response?.data?.data?.message || "Email has been sent",
          "success"
        );
        handleState({ resendEmailStatus: SUCCESS });
      } else {
        handleState({ resendEmailStatus: ERROR });
      }
      return response;
    } catch (error) {
      handleState({ resendEmailStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      return error;
    }
  };

  const fetchUserPermissions = async () => {
    try {
      handleState({ permissionsStatus: LOADING });
      const response = await getUserAccess();
      if (response?.data) {
        handleState({
          permissions: response.data?.data,
          permissionsStatus: SUCCESS,
        });
      }
    } catch (error) {
      console.error("Error fetching user permissions:", error);
      handleState({
        permissions: null,
        permissionsStatus: ERROR,
      });
    }
  };

  const handleSuccessfulLogin = async (response, access_token) => {
    localStorage.setItem("accessToken", access_token || response.data.data.access_token);
    localStorage.setItem("userId", response?.data?.data?.user?.id);
    localStorage.setItem(
      "isMentalHealthProfessional",
      response.data.data?.user?.roles?.some(
        (role) => role.name === "mental_health_provider"
      )
    );
    localStorage.setItem(
      "account_type",
      Boolean(isMentalHealthProfessional())
        ? "user"
        : response.data.data?.user?.account_type
    );
    localStorage.setItem(
      "post_account_type",
      Boolean(isMentalHealthProfessional())
        ? "user"
        : response.data.data?.user?.account_type
    );
    localStorage.setItem("isLoggedIn", "true");
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 1);
    cookies.set("isLoggedIn", "true", {
      expires: expirationDate,
      domain: `.${domain}`,
      secure: true,
    });

    handleState({
      loginStatus: SUCCESS,
      userData: response.data.data.user,
      isLoggedIn: true,
      account_type: Boolean(
        response.data.data?.user?.roles?.some(
          (role) => role.name === "mental_health_provider"
        )
      )
        ? "user"
        : response.data.data?.user?.account_type,
      post_account_type: Boolean(
        response.data.data?.user?.roles?.some(
          (role) => role.name === "mental_health_provider"
        )
      )
        ? "user"
        : response.data.data?.user?.account_type,
      isLogginBlocked: false,
      isVerificationStep: false, // Reset verification step state
      tempUserData: {}, // Clear temporary data
    });
    setStep(1);
    sendAlert("Logged in successfully", "success");
    
    // Fetch user permissions after successful login
    await fetchUserPermissions();
  };

  const handleSuccessfulRegistration = async (userData) => {
    localStorage.setItem("accessToken", userData.token);
    localStorage.setItem("userId", userData?.id);
    const isMentalHealthProf = userData?.roles?.some(
      (role) => role.name === "mental_health_provider"
    );
    localStorage.setItem(
      "isMentalHealthProfessional",
      isMentalHealthProf
    );
    const accountType = isMentalHealthProf
      ? "user"
      : userData?.account_type;
    localStorage.setItem("account_type", accountType);
    localStorage.setItem("post_account_type", accountType);
    localStorage.setItem("isLoggedIn", "true");
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 1);
    cookies.set("isLoggedIn", "true", {
      expires: expirationDate,
      domain: `.${domain}`,
      secure: true,
    });

    handleState({
      loginStatus: SUCCESS,
      userData: userData,
      isLoggedIn: true,
      account_type: accountType,
      post_account_type: accountType,
      isLogginBlocked: false,
      isVerificationStep: false, // Reset verification step state
      tempUserData: {}, // Clear temporary data
    });
    setStep(1);
    identifyUser(userData);
    sendAlert("Registration successful. Welcome to TTN!", "success");
    
    // Fetch user permissions after successful registration
    await fetchUserPermissions();
  };

  const handleLoginError = (error) => {
    handleState({ loginStatus: ERROR });
    if (error.response?.data?.error == "") {
      sendAlert(null, "error", true);
    } else {
      sendAlert(
        error.response?.data?.error || error.response?.data?.message,
        "error"
      );
    }
  };

  const handleBlockLogin = (error) => {
    if (!localStorage.getItem("loginBlockDate")) {
      localStorage.setItem("loginBlockDate", moment());
      handleState({
        isLogginBlocked: true,
        loginBlockDate: moment(),
      });
    }
  };

  const resentNewPassword = async (data) => {
    handleState({ forgetPasswordStatus: LOADING });
    handleState({ email: data?.email });
    try {
      const response = await resentPassword(data);
      if (response.status === 200) {
        setStep(2);
        localStorage.setItem("timerStart", Date.now().toString());
        localStorage.setItem("resetStep", 2);
        localStorage.setItem("email", data.email);
        sendAlert("Reset password email sent", "success");
        handleState({
          forgetPasswordStatus: SUCCESS,
        });
      }
      return response;
    } catch (error) {
      handleState({ forgetPasswordStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
    }
  };

  const forgotPassword = async (data) => {
    handleState({ forgetPasswordStatus: LOADING });
    handleState({ email: data?.email });
    try {
      const response = await forgetPassword(data);
      if (response.status === 200) {
        setStep(2);
        localStorage.setItem("timerStart", Date.now().toString());
        localStorage.setItem("resetStep", 2);
        localStorage.setItem("email", data.email);
        sendAlert("Reset password email sent", "success");
        handleState({
          forgetPasswordStatus: SUCCESS,
        });
      }
      return response;
    } catch (error) {
      handleState({ forgetPasswordStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
    }
  };

  const passwordReset = async (data) => {
    handleState({ resetPasswordStatus: LOADING });
    try {
      const response = await resetPassword(data);
      if (response.status === 200) {
        // The server ends every session on a reset, so forget this browser's
        // login too; otherwise the login page would bounce straight to home.
        localStorage.removeItem("accessToken");
        localStorage.removeItem("isLoggedIn");
        cookies.remove("isLoggedIn", { domain: `.${domain}` });
        sendAlert("Password has been reset successfully!", "success");
        handleState({
          resetPasswordStatus: SUCCESS,
          isLoggedIn: false,
        });
        if (step === 2) {
          sendAlert("Your request has been sent successfully", "success");
        }
      }
      return response;
    } catch (error) {
      handleState({ resetPasswordStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
    }
  };

  const resendEmailVerification = async (data) => {
    handleState({ resendEmailStatus: LOADING });
    try {
      const response = await resendEmail(data);
      sendAlert("Email has been sent", "success");
      handleState({ resendEmailStatus: SUCCESS });
      return response;
    } catch (error) {
      handleState({ resendEmailStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const updatePassword = async (data) => {
    handleState({ updatePasswordStatus: LOADING });
    try {
      const response = await changePassword(data);
      sendAlert("Password has been changed successfully!", "success");
      handleState({
        updatePasswordStatus: SUCCESS,
      });
      return response;
    } catch (error) {
      handleState({ updatePasswordStatus: ERROR });
      const errorsValues =
        error.response?.data?.errors &&
        Object.keys(error.response?.data?.errors || {}).map((key) => {
          if (typeof error.response?.data?.errors[key] === "object") {
            if (Array.isArray(error.response?.data?.errors[key])) {
              return error.response?.data?.errors[key];
            }
            return "";
          }
          return error.response?.data?.errors[key];
        });
      sendAlert(errorsValues || error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const logout = async () => {
    // Set flag to indicate we are logging out
    sessionStorage.setItem('isLoggingOut', 'true');

    localStorage.removeItem("accessToken");
    localStorage.removeItem("userData");
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("resetStep");
    localStorage.removeItem("account_type");
    localStorage.removeItem("onboardingData");
    localStorage.removeItem("resetStep");
    localStorage.removeItem("isMentalHealthProfessional");

    sessionStorage.removeItem("networkSortBy");
    sessionStorage.removeItem("networkSortBy");       // Double clear for Incognito tabs
    cookies.remove("isLoggedIn", { domain: `.${domain}` });
    handleState({
      loginStatus: "",
      userData: initialUserData,
      isLoggedIn: false,
      permissions: null,
      permissionsStatus: "",
    });
    resetMixpanel();
  };

  const handleDeactivateAccount = async () => {
    handleState({ deactivateAccountStatus: LOADING });
    try {
      const response = await deactivateAccount();
      handleState({
        updatePasswordStatus: SUCCESS,
      });
      return response;
    } catch (error) {
      handleState({ deactivateAccountStatus: ERROR });
      const errorsValues =
        error.response?.data?.errors &&
        Object.keys(error.response?.data?.errors || {}).map((key) => {
          if (typeof error.response?.data?.errors[key] === "object") {
            if (Array.isArray(error.response?.data?.errors[key])) {
              return error.response?.data?.errors[key];
            }
            return "";
          }
          return error.response?.data?.errors[key];
        });
      sendAlert(errorsValues || error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const handleDeactivateAccountFeedback = async (feedback) => {
    handleState({ deactivateAccountStatus: LOADING });
    try {
      const response = await deactivationFeedback(feedback);
      sendAlert(response.data?.data || "Feedback is submitted!", "success");
      handleState({
        updatePasswordStatus: SUCCESS,
      });
      logout();
      return response;
    } catch (error) {
      handleState({ deactivateAccountStatus: ERROR });
      const errorsValues =
        error.response?.data?.errors &&
        Object.keys(error.response?.data?.errors || {}).map((key) => {
          if (typeof error.response?.data?.errors[key] === "object") {
            if (Array.isArray(error.response?.data?.errors[key])) {
              return error.response?.data?.errors[key];
            }
            return "";
          }
          return error.response?.data?.errors[key];
        });
      sendAlert(errorsValues || error.response?.data?.message, "error");
      Promise.reject(error);
      logout();
    }
  };

  const updateAuthState = (field, value) => {
    handleState({ [field]: value });
  };

  const goBackLogin = () => {
    setStep(1);
  };

  // Always fetch fresh permissions on app initialization if user is logged in
  useEffect(() => {
    if (authState.isLoggedIn && authState.permissionsStatus !== LOADING) {
      fetchUserPermissions();
    }
  }, [authState.isLoggedIn]);

  useEffect(() => {
    if (localStorage.getItem("loginBlockDate")) {
      intervalId = setInterval(() => {
        const fiveMinutesAfter = moment(authState.loginBlockDate).add(
          30,
          "minutes"
        );
        const isBlocked = moment().isBefore(fiveMinutesAfter);
        const duration = moment.duration(fiveMinutesAfter.diff(moment()));
        const sec_num = Math.round(duration.asSeconds());
        let minutes = Math.floor(sec_num / 60);
        let seconds = sec_num - minutes * 60;

        if (minutes < 10) {
          minutes = "0" + minutes;
        }
        if (seconds < 10) {
          seconds = "0" + seconds;
        }
        handleState({
          isLogginBlocked: isBlocked,
          countDown: minutes + ":" + seconds,
        });
        if (!isBlocked) {
          handleState({
            isLogginBlocked: false,
          });
          localStorage.removeItem("loginBlockDate");
          clearInterval(intervalId);
        }
      }, 1000);
    } else {
      handleState({
        isLogginBlocked: false,
      });
      clearInterval(intervalId);
    }
  }, [authState.loginBlockDate]);

  const state = {
    ...authState,
    signIn,
    signInSSO,
    verifyUserToken,
    resentNewPassword,
    forgotPassword,
    verifyLoginCode,
    isMentalHealthProfessional,
    step,
    next,
    prev,
    goBackLogin,
    passwordReset,
    logout,
    resendEmailVerification,
    resendCodeVerification,
    updatePassword,
    handleDeactivateAccount,
    handleDeactivateAccountFeedback,
    updateAuthState,
    fetchUserPermissions,
    handleSuccessfulRegistration,
  };

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
